import * as THREE from 'three';

/**
 * Environment.ts — builds an image-based-lighting (IBL) reflection map from a
 * city's sky, so every PBR surface (car paint, glass, chrome rims, the ball)
 * has something REAL to reflect. Without this, metalness/clearcoat materials
 * reflect a black void and read as flat plastic — the single biggest reason the
 * pre-M35 cars looked dull.
 *
 * The map is generated with three's PMREMGenerator from a tiny in-memory sky
 * scene (a gradient dome + a bright sun disc) — so there is ZERO asset download,
 * it works fully offline, and the reflections always match the city we just
 * themed (a dusky NYC reflects orange; an overcast London reflects grey).
 *
 * Regenerated once per city load (never per frame), so it's cheap. The previous
 * texture is disposed each time to avoid leaking GPU memory across city changes.
 */
export interface EnvParams {
  /** Sky gradient — matches Theme.sky. */
  skyTop: number;
  skyHorizon: number;
  /** Directional-sun colour + intensity (drives the key reflection highlight). */
  sun: number;
  sunIntensity: number;
  /** Normalised direction TO the sun in world space (where the highlight sits). */
  sunDir: THREE.Vector3;
  /** Ground-bounce colour for downward reflections (matches Theme.hemiGround). */
  ground: number;
}

export class EnvironmentBuilder {
  private readonly pmrem: THREE.PMREMGenerator;
  private readonly scene = new THREE.Scene();
  private readonly domeMat: THREE.ShaderMaterial;
  private readonly sun: THREE.Mesh;
  private readonly sunMat: THREE.MeshBasicMaterial;
  private current: THREE.Texture | null = null;

  constructor(renderer: THREE.WebGLRenderer) {
    this.pmrem = new THREE.PMREMGenerator(renderer);

    // A gradient dome (inside-out sphere) — the same top→horizon→ground blend the
    // visible Sky uses, so reflections read as "the sky above this city".
    this.domeMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        topColor: { value: new THREE.Color() },
        horizonColor: { value: new THREE.Color() },
        groundColor: { value: new THREE.Color() },
      },
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 topColor;
        uniform vec3 horizonColor;
        uniform vec3 groundColor;
        varying vec3 vDir;
        void main() {
          float y = vDir.y;
          vec3 c = y >= 0.0
            ? mix(horizonColor, topColor, pow(clamp(y, 0.0, 1.0), 0.5))
            : mix(horizonColor, groundColor, clamp(-y * 1.5, 0.0, 1.0));
          gl_FragColor = vec4(c, 1.0);
        }
      `,
    });
    const dome = new THREE.Mesh(new THREE.SphereGeometry(50, 32, 16), this.domeMat);
    dome.frustumCulled = false;
    this.scene.add(dome);

    // A bright sun disc — overdriven past white (HDR) so it leaves a hot specular
    // highlight on paint and chrome instead of a dull grey smear.
    this.sunMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.sun = new THREE.Mesh(new THREE.SphereGeometry(2.5, 16, 16), this.sunMat);
    this.scene.add(this.sun);
  }

  /** Build (and own) a fresh PMREM environment texture for the given sky. */
  generate(p: EnvParams): THREE.Texture {
    (this.domeMat.uniforms.topColor.value as THREE.Color).set(p.skyTop);
    (this.domeMat.uniforms.horizonColor.value as THREE.Color).set(p.skyHorizon);
    (this.domeMat.uniforms.groundColor.value as THREE.Color).set(p.ground);

    this.sun.position.copy(p.sunDir).normalize().multiplyScalar(40);
    // Push the sun colour above 1.0 so it reads as a light source, not a sticker.
    this.sunMat.color.set(p.sun).multiplyScalar(1 + Math.max(0, p.sunIntensity) * 1.4);

    this.current?.dispose();
    this.current = this.pmrem.fromScene(this.scene, 0, 0.1, 100).texture;
    return this.current;
  }

  dispose(): void {
    this.current?.dispose();
    this.pmrem.dispose();
    this.domeMat.dispose();
    this.sunMat.dispose();
    (this.sun.geometry as THREE.BufferGeometry).dispose();
  }
}
